import React, { useRef } from 'react';
import { useQcDeviceHistory } from '../../../hooks/useQcDeviceHistory';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt, faCheckCircle, faTimesCircle, faMinusCircle, faInbox
} from '@fortawesome/free-solid-svg-icons';
import './QCTimelineTab.css';
import TooltipCustom from '../../EpikBoxes/EpikBoxDetails/TooltipCustom';
import SpinnerCircles from '../../Spinner/SpinerCircles';

// Helper: Generate consistent colors from a string
const toColors = (userName) => {
  let hash = 0;
  for (let i = 0; i < userName.length; i++) {
    hash = userName.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return {
    light: `hsl(${hue}, 70%, 90%)`,
    dark: `hsl(${hue}, 70%, 30%)`
  };
};

// Helper: Add opacity to hex color
const addOpacityToHex = (hexColor, opacity = 0.1) => {
  let hex = hexColor.replace('#', '');
  if (hex.length === 3) hex = hex.split('').map(x => x + x).join('');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const Avatar1 = ({ user, tipId }) => {
  let initials = 'S';
  let fullName = 'System';
  if (user) {
    if (user.name) {
      const parts = user.name.trim().split(' ');
      if (parts.length > 1) {
        initials = parts[0][0].toUpperCase() + parts[parts.length - 1][0].toUpperCase();
      } else {
        initials = parts[0][0].toUpperCase();
      }
      fullName = user.name;
    } else if (user.email) {
      initials = user.email[0].toUpperCase();
      fullName = user.email;
    }
  }
  const userColors = toColors(user?.name || user?.email || 'System');
  return (
    <>
      <div
        className="avatar-circle"
        id={tipId}
        style={{ background: userColors.light, color: userColors.dark, fontWeight: 600, fontSize: 16, width: 36, height: 36, minWidth: 36, minHeight: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 16 }}
      >
        {initials}
      </div>
      <TooltipCustom tipId={tipId} displayData={fullName} placement="top" />
    </>
  );
};

const formatTimeOnly = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const DateGroup = ({ date }) => {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const isToday = date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
  const isYesterday = date.getDate() === yesterday.getDate() && date.getMonth() === yesterday.getMonth() && date.getFullYear() === yesterday.getFullYear();
  const isCurrentYear = date.getFullYear() === today.getFullYear();
  let displayDate;
  if (isToday) displayDate = 'Today';
  else if (isYesterday) displayDate = 'Yesterday';
  else displayDate = date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: isCurrentYear ? undefined : 'numeric' });
  return (
    <div style={{ display: 'flex', justifyContent: 'center', margin: '18px 0' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid #e5e7eb', borderRadius: 16, padding: '3px 16px', fontSize: 15, color: '#6b7280', fontWeight: 500 }}>
        <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: 8, fontSize: 16 }} />
        {displayDate}
      </span>
    </div>
  );
};

function stepNameOf(id) {
  switch (id) {
    case "device-active": return "Device firmware";
    case "device-firmware-updated": return "Device active";
    case "ata-firmware-updated": return "EPI";
    case "sim1-active": return "SIM 1 active";
    case "sim2-active": return "SIM 2 active";
    case "sim-check-failover": return "Failover";
    case "power-cycle": return "Power cycle";
    default: return id.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }
}

function getInspectionLabel(name) {
  const knownLabels = {
    powerLight: "Power light",
    ata1Light: "ATA 1 (power, Internet, network)",
    ata2Light: "ATA 2 (power, Internet)",
    networkLight: "Eth0/1 activity lights",
    batteryPower: "Battery power",
    batteryPercent: "Battery percentage",
    powerDisconnect: "Power disconnect",
    chasis: "Case: No bends, dents, cracks, breaks",
    mountingBrackets: "Mounting brackets: No bends, dents, cracks, breaks",
    phonePort: "RJ-11"
  };
  if (knownLabels[name]) return knownLabels[name];
  return name.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
}

function friendlyStageName(stage) {
  switch (stage) {
    case 'sim': return 'SIM activation stage';
    case 'software': return 'Software stage';
    case 'hardware': return 'Hardware stage';
    case 'shipping': return 'Shipping stage';
    case 'engineering': return 'Engineering bin';
    case 'repair': return 'Repair bin';
    case 'failure': return 'Failure bin';
    default: return stage;
  }
}

const EventContent = ({ event }) => {
  const { type, data, user } = event;
  const userName = user?.name || user?.email || 'System';
  const userColors = toColors(userName);
  if (type === 'note' || (type === 'log' && data?.level === 'note')) {
    return (
      <div>
        {data?.message && (
          <div
            className="font-normal rounded-2xl text-sm inline-block max-w-96 px-3 py-1.5"
            style={{
              color: userColors.dark,
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              backgroundColor: addOpacityToHex(userColors.light, .5),
            }}
          >
            {decodeURIComponent(data.message)}
          </div>
        )}
      </div>
    );
  }
  switch (type) {
    case 'device_config':
      return (
        <div className="break-words max-w-full">
          <div className="font-medium">Device configured for SIM activation</div>
          <div className="text-sm text-gray-500 mt-1" style={{ color: '#a3a3a3', fontSize: 12, marginLeft: 32, marginTop: 2 }}>
            <div>Plan: {data?.plan || 'Unknown'}</div>
            <div>Region: {data?.region || 'Unknown'}</div>
          </div>
        </div>
      );
    case 'sim_activation': {
      const isSuccess = data?.success === true;
      const isFailed = data?.status === 'failed';
      return (
        <div className="break-words max-w-full">
          <span style={{ display: 'inline-flex', alignItems: 'center', marginBottom: 2 }}>
            <FontAwesomeIcon icon={isSuccess ? faCheckCircle : faTimesCircle} size="sm" style={{ color: isSuccess ? '#22c55e' : '#ef4444', marginRight: 8 }} />
            <span style={{ fontWeight: 500, color: isSuccess ? '#22c55e' : '#ef4444' }}>
              SIM {data?.slot || '?'} activation {isSuccess ? 'succeeded' : 'failed'}
            </span>
          </span>
          {data?.startedAt && (
            <div style={{ color: '#a3a3a3', fontSize: 12, marginLeft: 32, marginTop: 2 }}>
              Started: {new Date(data.startedAt).toLocaleString()}
            </div>
          )}
          {data?.completedAt && (
            <div style={{ color: '#a3a3a3', fontSize: 12, marginLeft: 32, marginTop: 2 }}>
              Completed: {new Date(data.completedAt).toLocaleString()}
            </div>
          )}
        </div>
      );
    }
    case 'inspection':
      return (
        <div className="break-words max-w-full">
          <div className="font-medium">Hardware inspection {data?.passed ? 'passed' : 'failed'}</div>
          {data?.checks && (
            <div className="text-sm text-gray-500" style={{ marginTop: 6 }}>
              {Object.entries(data.checks).map(([key, value]) => (
                <div key={key} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 6 }}>
                  <span style={{ display: 'flex', alignItems: 'center', minWidth: 24 }}>
                    {value ? (
                      <FontAwesomeIcon icon={faCheckCircle} size="sm" style={{ color: '#22c55e', marginRight: 8 }} />
                    ) : data?.passed ? (
                      <FontAwesomeIcon icon={faMinusCircle} size="sm" style={{ color: '#a3a3a3', marginRight: 8 }} />
                    ) : (
                      <FontAwesomeIcon icon={faTimesCircle} size="sm" style={{ color: '#ef4444', marginRight: 8 }} />
                    )}
                  </span>
                  <span style={{ fontWeight: 500, color: value ? '#22c55e' : (data?.passed ? '#a3a3a3' : '#ef4444') }}>{getInspectionLabel(key)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    case 'stage':
      return (
        <div className="font-medium break-words max-w-full">Moved to {friendlyStageName(data?.stage)}</div>
      );
    case 'location':
      return (
        <div className="font-medium break-words max-w-full">Rack location changed to {data?.location}</div>
      );
    case 'test':
      return (
        <div className="break-words max-w-full">
          <div className="font-medium" style={{ marginBottom: 4 }}>
            Software test {data?.attempt} {data?.status === 'passed' ? 'passed' : data?.status}
          </div>
          {data?.results && data.results.length > 0 && (
            <div className="text-sm" style={{ marginTop: 2 }}>
              {data.results.map((result, idx) => (
                <div key={result._id || idx} style={{ display: 'flex', flexDirection: 'column', marginBottom: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ display: 'flex', alignItems: 'center', minWidth: 24 }}>
                      {result.passed ? (
                        <FontAwesomeIcon icon={faCheckCircle} size="sm" style={{ color: '#22c55e', marginRight: 8 }} />
                      ) : (
                        <FontAwesomeIcon icon={faTimesCircle} size="sm" style={{ color: '#ef4444', marginRight: 8 }} />
                      )}
                    </span>
                    <span style={{ fontWeight: 500, color: result.passed ? '#22c55e' : '#ef4444' }}>{stepNameOf(result.name)}</span>
                  </div>
                  {result.reason && (
                    <div style={{ color: '#a3a3a3', fontSize: 13, marginLeft: 32, marginTop: 1 }}>{result.reason}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    case 'log':
      return (
        <div className="break-words max-w-full">
          {data?.message && (
            <div className="font-normal break-words">
              {decodeURIComponent(data.message)}
            </div>
          )}
        </div>
      );
    default:
      return <div className="font-medium break-words max-w-full">{type} event</div>;
  }
};

const TimelineEvent = ({ event, prevEvent }) => {
  const { eventTime, user } = event;
  let rawTipId = `avatar-tip-${eventTime}-${user?.name || user?.email || 'system'}`;
  let tipId = rawTipId.replace(/[^a-zA-Z0-9_-]/g, '-');
  const isSystem = !user || (!user.name && !user.email);
  return (
    <div className="timeline-event-row" style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 8 }}>
      <div style={{ marginTop: 2 }}>
        <Avatar1 user={user} tipId={tipId} />
      </div>
      <div className="timeline-event-card" style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 2px rgba(0,0,0,0.03)', padding: '12px 16px', marginLeft: 0, flex: 1, minWidth: 0, display: 'flex', flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <EventContent event={event} user={user} />
        </div>
        <div style={{ marginLeft: 16, minWidth: 60, textAlign: 'right', alignSelf: 'flex-start' }}>
          <span className="text-[12px] text-gray-500 flex-shrink-0 mx-2 text-nowrap" style={{ fontSize: 13, color: '#888' }}>{formatTimeOnly(eventTime)}</span>
        </div>
      </div>
    </div>
  );
};

export default function QCTimelineTab({ serialNumber }) {
  const { isLoading, data, error } = useQcDeviceHistory(serialNumber);
  const timelineEndRef = useRef(null);

  // Group timeline events by date
  const groupTimelineByDate = (timeline = []) => {
    const groups = (timeline || []).reduce((acc, event) => {
      const date = new Date(event.eventTime);
      const dateKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
      if (!acc[dateKey]) {
        acc[dateKey] = { date, events: [] };
      }
      acc[dateKey].events.push(event);
      return acc;
    }, {});
    Object.values(groups).forEach(group => {
      group.events.sort((a, b) => new Date(a.eventTime) - new Date(b.eventTime));
    });
    return Object.values(groups).sort((a, b) => a.date - b.date);
  };
  const timeline = data?.timeline || [];
  const filteredGroups = groupTimelineByDate(timeline);
  return (
    <div className="deviceHistoryScrollBar h-full overflow-y-auto p-0 m-0 pr-5 pl-5 rounded-md bg-stone-50">
      {isLoading ? (
        <div className='d-flex align-items-center justify-content-center' style={{ height: 500 }}>
            <SpinnerCircles red />
        </div>
      ) : error ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            minHeight: 300,
          }}
        >
          <div
            style={{
              background: '#fee2e2',
              color: '#b91c1c',
              borderRadius: 12,
              padding: '24px 32px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              maxWidth: 400,
            }}
          >
            <FontAwesomeIcon icon={faTimesCircle} size="2x" style={{ marginBottom: 12 }} />
            <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 6 }}>Something went wrong</div>
            <div style={{ fontSize: 15, textAlign: 'center', wordBreak: 'break-word' }}>
              {typeof error === 'string' ? error : error?.toString()}
            </div>
          </div>
        </div>
      ) : timeline.length === 0 ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            minHeight: 220,
          }}
        >
          <div
            style={{
              background: '#f3f4f6',
              color: '#6b7280',
              borderRadius: 12,
              padding: '24px 32px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
              maxWidth: 400,
            }}
          >
            <FontAwesomeIcon icon={faInbox} size="2x" style={{ marginBottom: 12, color: '#9ca3af' }} />
            <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 6 }}>No QC history found for this device</div>
          </div>
        </div>
      ) : filteredGroups.length === 0 ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            minHeight: 220,
          }}
        >
          <div
            style={{
              background: '#f3f4f6',
              color: '#6b7280',
              borderRadius: 12,
              padding: '24px 32px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
              maxWidth: 400,
            }}
          >
            <FontAwesomeIcon icon={faInbox} size="2x" style={{ marginBottom: 12, color: '#9ca3af' }} />
            <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 6 }}>No events found for this device</div>
          </div>
        </div>
      ) : (
        <div className="w-full flex flex-col" style={{ padding: 0, margin: 0 }}>
          {filteredGroups.map((group, groupIndex) => (
            <div key={`group-${groupIndex}`}>
              <DateGroup date={group.date} />
              {group.events.map((event, eventIndex) => (
                <TimelineEvent
                  key={`event-${groupIndex}-${eventIndex}`}
                  event={event}
                  prevEvent={eventIndex > 0 ? group.events[eventIndex - 1] : null}
                />
              ))}
            </div>
          ))}
          <div ref={timelineEndRef} className="h-1" />
        </div>
      )}
    </div>
  );
}